import { TextField, VFFTextFieldAdapter } from '@eventbrite/design-ops-ds';
import { Debounced } from '@eventbrite/eds-debounced';
import { InputField } from '@eventbrite/eds-input-field';
import { ValidationFormField } from '@eventbrite/eds-validation-form-field';
import { shouldDisplayError } from '@eventbrite/redux-form-tools';
import classNames from 'classnames';
import React from 'react';
import { isPromoCodeOpenByDefault } from '../../utils/statsig';
import { getValidationAdornment } from './helpers';

const DEBOUNCE_TIME = 500;

export interface PromoCodeInputFieldProps {
    cta: string;
    disablePromoCodeAutoFocus: boolean;
    handleSubmit: () => void;
    helperText: string;
    inputLabel: string;
    inputPlaceholder: string;
    isCodeSuccessful: boolean;
    onChange: () => void;
    query: () => void;
    submitFailed: boolean;
}

export const PromoCodeInputField: React.FunctionComponent<
    PromoCodeInputFieldProps
> = ({
    cta,
    disablePromoCodeAutoFocus,
    handleSubmit,
    helperText,
    inputLabel,
    inputPlaceholder,
    isCodeSuccessful,
    onChange,
    query,
    submitFailed,
}) => {
    const formClassName = classNames('responsive-form', 'eds-g-group');

    const adornment = getValidationAdornment(submitFailed, isCodeSuccessful);

    const inputField = isPromoCodeOpenByDefault() ? (
        <VFFTextFieldAdapter onChange={onChange}>
            <TextField
                data-automation="promo-code-field"
                data-spec="promo-code-field"
                disabled={isCodeSuccessful}
                id="promo-code-field"
                label={inputLabel}
                placeholder={inputPlaceholder}
                suffix={cta}
                type="text"
                validationAdornment={adornment}
                helperText={helperText}
            />
        </VFFTextFieldAdapter>
    ) : (
        <Debounced
            wrappedComponent={InputField}
            callbacksToDebounce={['onChange']}
            debounceTime={DEBOUNCE_TIME}
            id="promo-code-field"
            value={query}
            role="textbox"
            style="static"
            data-automation="promo-code-field"
            data-spec="promo-code-field"
            label={inputLabel}
            placeholder={inputPlaceholder}
            suffix={cta}
            onChange={onChange}
            defaultIsActive={!disablePromoCodeAutoFocus}
        />
    );

    let timer: NodeJS.Timeout | null = null;
    const debouncedSubmit = (e: any) => {
        e.preventDefault();
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        timer = setTimeout(handleSubmit, DEBOUNCE_TIME);
    };

    return (
        <form
            className={formClassName}
            onSubmit={debouncedSubmit}
            data-automation="promo-code-form"
            data-testid="promo-code-form"
        >
            <div className="eds-g-cell eds-g-cell-12-12">
                <ValidationFormField
                    name="promo"
                    shouldDisplayError={shouldDisplayError}
                    v2={true}
                >
                    {inputField}
                </ValidationFormField>
            </div>
        </form>
    );
};
